import {
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { usePrivacy } from 'contexts/PrivacyContext';
import useAuth from 'contexts/useAuth';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { maskCurrency } from 'utils/number';

const BalanceLine = (props: { label: string; value: number | string; emphasis?: boolean }) => {
  const labelColor = useColorModeValue('secondaryGray.700', 'whiteAlpha.700');
  const valueColor = useColorModeValue('secondaryGray.900', 'white');

  return (
    <HStack
      justify="space-between"
      spacing={6}
    >
      <Text
        fontSize="sm"
        color={labelColor}
      >
        {props.label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight={props.emphasis ? '700' : '500'}
        color={valueColor}
        whiteSpace="nowrap"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {maskCurrency(props.value)}
      </Text>
    </HStack>
  );
};

/**
 * Saldo disponível do topo, com rótulo e o botão de ocultar valores.
 *
 * O detalhamento vive num Popover clicável, e não num Tooltip: os outros dois saldos
 * (em liberação e bloqueado) eram descobríveis só por hover, o que não existe no toque.
 */
export default function BalanceSummary() {
  const { userData } = useAuth();
  const { valuesHidden, toggleValues } = usePrivacy();

  const labelColor = useColorModeValue('secondaryGray.700', 'whiteAlpha.700');
  const valueColor = useColorModeValue('secondaryGray.900', 'white');
  const popoverBg = useColorModeValue('white', 'navy.800');
  const hoverBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  // Com os valores ocultos a cor não pode denunciar o sinal — o teste de privacidade já
  // garante que o "-" não aparece no texto.
  const isNegative = !valuesHidden && Number(userData?.balance ?? 0) < 0;

  return (
    <HStack
      spacing={1}
      me={2}
    >
      <Popover
        placement="bottom-end"
        trigger="click"
      >
        <PopoverTrigger>
          <VStack
            spacing={0}
            align="flex-end"
            cursor="pointer"
            borderRadius="8px"
            px={2}
            py={1}
            _hover={{ bg: hoverBg }}
          >
            <Text
              fontSize="2xs"
              lineHeight="1.1"
              color={labelColor}
              whiteSpace="nowrap"
            >
              Saldo disponível
            </Text>
            <Text
              fontSize="md"
              fontWeight="700"
              lineHeight="1.2"
              whiteSpace="nowrap"
              color={isNegative ? 'red.500' : valueColor}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {maskCurrency(userData?.balance)}
            </Text>
          </VStack>
        </PopoverTrigger>
        <PopoverContent
          bg={popoverBg}
          borderRadius="16px"
          w="auto"
          minW="260px"
          _focus={{ boxShadow: 'md' }}
        >
          <PopoverArrow bg={popoverBg} />
          <PopoverBody p={4}>
            <VStack
              align="stretch"
              spacing={2}
            >
              <BalanceLine
                label="Saldo disponível"
                value={userData?.balance}
                emphasis
              />
              <BalanceLine
                label="Em liberação"
                value={userData?.balancePending}
              />
              <BalanceLine
                label="Saldo bloqueado"
                value={userData?.amountLocked}
              />
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <IconButton
        aria-label={valuesHidden ? 'Mostrar valores' : 'Ocultar valores'}
        title={valuesHidden ? 'Mostrar valores' : 'Ocultar valores'}
        icon={valuesHidden ? <MdVisibilityOff /> : <MdVisibility />}
        onClick={toggleValues}
        variant="ghost"
        size="sm"
        color={labelColor}
        _hover={{ bg: hoverBg }}
      />
    </HStack>
  );
}
